import { Box, Typography } from '@mui/material';
import type { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPagination } from '~/components/data-grid';
import { formatDateOnly } from '~/lib/datetime';
import { CardPaper, useEventMetricColumns } from '../lib';
import type { PlayoutReport__EventAggregate as EventAggregateRow } from '../queries/PlayoutReport.generated';
import { ReportDataGrid } from './ReportDataGrid';

interface ByEventListCardProps {
  rows: ReadonlyArray<EventAggregateRow>;
  onEventClick: (eventId: number) => void;
}

export const ByEventListCard = ({ rows, onEventClick }: ByEventListCardProps) => {
  const { t } = useTranslation();
  const metricColumns = useEventMetricColumns<EventAggregateRow>({ averages: false });

  const columns = useMemo<GridColDef<EventAggregateRow>[]>(
    () => [
      {
        field: 'event',
        headerName: t('pages.playout_reports.by_event_list.columns.event'),
        flex: 1.5,
        minWidth: 180,
        hideable: false,
      },
      {
        field: 'eventStartTime',
        headerName: t('pages.playout_reports.by_event_list.columns.date'),
        width: 140,
        valueFormatter: (_, { eventStartTime }) =>
          (eventStartTime ? formatDateOnly(eventStartTime, { showWeekday: false }) : null) ?? '—',
      },
      ...metricColumns.filter((c) => c.field !== 'eventCount'),
    ],
    [t, metricColumns],
  );

  return (
    <CardPaper sx={{ pb: 0 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography component="h2" variant="subtitle1" fontWeight={600}>
          {t('pages.playout_reports.by_event_list.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {t('pages.playout_reports.by_event_list.count', { count: rows.length })}
        </Typography>
      </Box>
      <ReportDataGrid
        columnStateKey="byEventList"
        columns={columns}
        rows={rows}
        disableRowSelectionOnClick
        getRowId={(row: EventAggregateRow) => row.eventId}
        onRowClick={({ row }: GridRowParams<EventAggregateRow>) => onEventClick(row.eventId)}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
          sorting: { sortModel: [{ field: 'eventStartTime', sort: 'desc' }] },
        }}
        slots={{ pagination: DataGridPagination }}
      />
    </CardPaper>
  );
};
